import { SHOP_URL } from '../constants';

/**
 * Pure cashback presentation layer: state derivation + currency/date formatting.
 * Reads the real /api/cashback slice fields (camelCase, SHOPWEB-693) and emits the
 * named string/bool props CashbackWidget consumes. No React, no fetching, no side effects.
 */

const PAYOUT_DATE_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

// Format a major-unit amount as currency. Returns '' for a non-finite amount (malformed payload)
// so the widget hides the figure instead of rendering "$NaN". Falls back to USD on a bad ISO code.
export const formatCurrency = (amount, currency) => {
  if (!Number.isFinite(amount)) return '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch (e) {
    // Invalid currency code — render with USD so the figure still shows.
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
};

/* Format an ISO date for display. Repo i18n.language values use underscores (zh_Hans, fr_CA); the
   _->- normalization makes them valid BCP-47. The try/catch is a backstop that falls back to 'en'
   for any other non-BCP-47 tag. Empty/invalid dates return '' so the widget's payout-line guard
   hides the line. */
export const formatPayoutDate = (isoDate, locale) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const normalized =
    typeof locale === 'string' ? locale.replace(/_/g, '-') : 'en';
  try {
    return new Intl.DateTimeFormat(normalized, PAYOUT_DATE_OPTIONS).format(
      date,
    );
  } catch (e) {
    // Non-BCP-47 locale tag — retry with English.
    return new Intl.DateTimeFormat('en', PAYOUT_DATE_OPTIONS).format(date);
  }
};

/* First-match-wins state machine over the real cashback slice fields. payout-failed (the ninth
   widget state) is deferred and never emitted in v1. payoutInProgress is not read at all — a
   payload with it set is classified by balance/pending/sent like any other. */
const deriveState = (cashback, threshold) => {
  const {
    cashBackLifetime = 0,
    availableBalance = 0,
    cashBackPending = 0,
    payoutSentDate = null,
    isFetching = false,
    error = null,
    lastFetched = null,
  } = cashback;

  const hasCache = lastFetched != null;

  if (isFetching && !hasCache) return 'loading';
  if (error && !hasCache) return 'error';
  if (payoutSentDate != null) return 'payout-sent';
  if (availableBalance > 0 && cashBackPending > 0) return 'available-pending';
  if (availableBalance > 0 && availableBalance < threshold)
    return 'below-threshold';
  if (availableBalance === 0 && cashBackLifetime > 0) return 'zero';
  if (
    cashBackLifetime === 0 &&
    availableBalance === 0 &&
    cashBackPending === 0 &&
    payoutSentDate == null
  )
    return 'first-time';
  return 'default';
};

/**
 * Build the full CashbackWidget prop view from the cashback slice.
 * @param {object} cashback - the cashback Redux slice plus i18n `locale`.
 * @param {object} flags - feature flags (reserved; not read in v1).
 * @param {number} threshold - CASHBACK_PAYOUT_THRESHOLD.
 * @param {function} t - i18n translator for the CTA label.
 */
export const deriveCashbackView = (cashback, flags, threshold, t) => {
  const {
    availableBalance = 0,
    cashBackPending = 0,
    nextPayoutDate = null,
    payoutSentDate = null,
    currency = 'USD',
    locale = 'en',
  } = cashback;

  return {
    state: deriveState(cashback, threshold),
    balance: formatCurrency(availableBalance, currency),
    pendingAmount: formatCurrency(cashBackPending, currency),
    payoutDate: formatPayoutDate(nextPayoutDate, locale),
    sentDate: formatPayoutDate(payoutSentDate, locale),
    thresholdAmount: formatCurrency(threshold, currency),
    managementHref: `${SHOP_URL}/cash-back/purchases`,
    ctaLabel: t ? t('cashback.cta.default') : undefined,
  };
};
